import type {Property} from "../types";

type PropertyAddressSource = {
  number?: number | string | null;
  street?: string | null;
  unit_number?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  /** @deprecated API read alias — normalize on ingest */
  zip_code?: string | number | null;
};

/** Resolve postal code from API (`zipcode`) or legacy `zip_code`. */
export function resolvePropertyZipcode(
  property: Pick<PropertyAddressSource, "zipcode" | "zip_code">,
): string {
  const zip = property.zipcode ?? property.zip_code;
  if (zip == null) return "";
  return String(zip).trim();
}

/** `number street, unit_number, city, state, zipcode` — omits empty segments. */
export function formatPropertyAddressLine(property: PropertyAddressSource): string {
  const streetLine = [property.number, property.street]
    .filter((value) => value != null && String(value).trim() !== "")
    .map(String)
    .join(" ")
    .trim();

  const parts = [
    streetLine || null,
    property.unit_number?.trim() || null,
    property.city?.trim() || null,
    property.state?.trim() || null,
    resolvePropertyZipcode(property) || null,
  ].filter((part): part is string => Boolean(part));

  return parts.join(", ");
}

export function normalizePropertyAddressFields<T extends PropertyAddressSource>(
  row: T,
): T & Pick<Property, "zipcode" | "unit_number"> {
  const {zip_code: _legacyZip, ...rest} = row as T & {zip_code?: string | number | null};
  return {
    ...rest,
    zipcode: resolvePropertyZipcode(row),
    unit_number: row.unit_number?.trim() || null,
  } as T & Pick<Property, "zipcode" | "unit_number">;
}

export function normalizePropertyAddressForWrite(fields: {
  street: string;
  number: string | number;
  unit_number?: string | null;
  city: string;
  state: string;
  zipcode: string;
}) {
  return {
    street: fields.street,
    number: typeof fields.number === "number" ? fields.number : Number(fields.number),
    unit_number: fields.unit_number?.trim() || null,
    city: fields.city,
    state: fields.state,
    zipcode: fields.zipcode.trim(),
  };
}

export function normalizeProperty<T extends Property & {zip_code?: string | number | null}>(
  row: T,
): Property {
  return normalizePropertyAddressFields(row) as Property;
}
